import { Button, Checkbox, DatePicker, Form, Select, Space, Tag, Tooltip } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePermissions } from '@/shared/auth/usePermissions'
import { rangeToIso } from '@/shared/lib/dayRange'
import { FilterDrawer } from '@/shared/ui/FilterDrawer'
import { STATE_ALL, STATE_OPEN_ONLY } from '@/shared/ui/table/closedFilter'
import type { TableState } from '@/shared/ui/table/tableState'
import { useTableUrlState } from '@/shared/ui/table/useTableUrlState'
import { fetchVenues } from '@/features/venue/venueApi'
import {
  LEAD_SOURCES,
  LEAD_STATUSES,
  TICKET_CATEGORIES,
  TICKET_PRIORITIES,
  TICKET_STATUSES,
} from './crmApi'

/**
 * CRM ro'yxatlarining KO'RINADIGAN filtrlari.
 *
 * <h2>⚠️ Holat bu yerda SAQLANMAYDI</h2>
 * Uchala panel ham {@code useTableUrlState} dan o'qiydi va unga yozadi —
 * ya'ni `DataTable` bilan BITTA holatni baham ko'radi. O'z
 * {@code useState} i ikkinchi haqiqat manbai bo'lardi va birinchi qayta
 * chizishdayoq jadval bilan ajralib ketardi ({@code SubscriberFilterBar}
 * dagi bir xil qaror).
 *
 * <h2>⚠️ Filtr SERVERGA boradi</h2>
 * Har uch endpoint filtrni O'ZI qo'llaydi ({@code LeadQuery},
 * {@code TicketQuery}, {@code InteractionQuery}) va faqat bir sahifa
 * qaytaradi. Mijozda filtrlash JORIY sahifani kesardi va operator uni
 * butun ro'yxat deb o'qirdi.
 */

/** URL holatida filtr qiymati DOIM massiv, tanlagich esa yakka qiymat kutadi */
function single(value: readonly string[] | undefined): string | undefined {
  return value?.[0]
}

type Filters = TableState['filters']

interface PatchApi {
  filters: Filters
  set: (key: string, value: string | undefined) => void
  /** Bir necha filtrni BITTA so'rov bilan topshiradi (`FilterDrawer` uchun) */
  commit: (next: Filters) => void
  clear: () => void
}

function useFilterPatch(): PatchApi {
  const { state, patch } = useTableUrlState()
  // ⚠️ Filtr o'zgarganda sahifa BIRINCHIGA qaytadi: 5-sahifada turib
  // filtr qo'yilganda natija 2 sahifa bo'lsa, ekran bo'sh chiqardi va
  // operator "hech narsa topilmadi" deb o'qirdi.
  const commit = (next: Filters) => patch({ page: 1, filters: next })
  return {
    filters: state.filters,
    set: (key, value) => commit({ ...state.filters, [key]: value ? [value] : [] }),
    commit,
    clear: () => commit({}),
  }
}

/**
 * "Tozalash" faqat filtr BOR bo'lganda chiqadi va nechtaligini aytadi.
 * Doim ko'rinadigan tugma foydasiz, yashirini esa topilmaydi.
 */
function ClearFilters({ count, onClear }: { count: number; onClear: () => void }) {
  const { t } = useTranslation()
  if (count === 0) return null
  return (
    <Space size={4}>
      <Tag color="blue">{t('dataTable.activeFilters', { count })}</Tag>
      <Button size="small" type="link" onClick={onClear}>
        {t('dataTable.clearFilters')}
      </Button>
    </Space>
  )
}

/**
 * Holat tanlagichining ro'yxati — ikki MAXSUS band bilan (ADR-033).
 *
 * ⚠️ «Yopilganlarsiz» — SUKUT ko'rinish va u ro'yxatda KO'RINADI.
 * `allowClear` bilan bo'sh qoldirish «filtr qo'yilmagan» deb o'qilardi,
 * ro'yxat esa aslida torayib turgan bo'lardi.
 *
 * ⚠️ «Barcha holatlar» — yopilganni ko'rishning yagona yo'li; u
 * serverga `includeClosed` bayrog'i bo'lib boradi.
 */
function closedAwareOptions(
  openOnly: string,
  all: string,
  statuses: readonly { value: string; label: string }[],
) {
  return [{ value: STATE_OPEN_ONLY, label: openOnly }, ...statuses, { value: STATE_ALL, label: all }]
}

export function LeadFilterBar() {
  const { t } = useTranslation()
  const { filters, set, clear } = useFilterPatch()
  const status = single(filters['status']) ?? STATE_OPEN_ONLY
  const source = single(filters['source'])

  return (
    <Space wrap size="small">
      <Tooltip title={t('dataTable.closedHiddenHint')}>
        <Select
          style={{ minWidth: 175 }}
          aria-label={t('subscribers.columns.status')}
          value={status}
          /* Sukut tanlov URL'ga YOZILMAYDI: aks holda «filtr topilmadi»
             bo'sh holati hech qachon ochilmagan filtr uchun chiqardi. */
          onChange={(v: string) => set('status', v === STATE_OPEN_ONLY ? undefined : v)}
          options={closedAwareOptions(
            t('dataTable.stateOpenOnly'),
            t('dataTable.stateAll'),
            LEAD_STATUSES.map((s) => ({ value: s, label: t(`crm.leadStatus.${s}`) })),
          )}
        />
      </Tooltip>
      <Select
        allowClear
        style={{ minWidth: 150 }}
        placeholder={t('crm.lead.source')}
        value={source}
        onChange={(v?: string) => set('source', v)}
        options={LEAD_SOURCES.map((s) => ({ value: s, label: t(`crm.leadSource.${s}`) }))}
      />
      <ClearFilters
        count={[status === STATE_OPEN_ONLY ? undefined : status, source].filter(Boolean).length}
        onClear={clear}
      />
    </Space>
  )
}

/**
 * Murojaatlar filtri — to'rtta shart.
 *
 * ⚠️ HOLAT tashqarida, qolgan uchtasi PANELDA (egasi qarori 2026-08-20).
 * To'rtta boshqaruv qatorni ikkiga bo'lardi; hammasini yashirish esa
 * qo'llab-quvvatlash xodimini «yangi / ish jarayonida» ni ajratish uchun
 * har safar panel ochishga majburlardi.
 */
export function TicketFilterBar() {
  const { t } = useTranslation()
  const { filters, set, commit } = useFilterPatch()
  const status = single(filters['status']) ?? STATE_OPEN_ONLY
  const category = single(filters['category'])
  const priority = single(filters['priority'])
  const overdue = single(filters['overdue']) === 'yes'
  const ackOverdue = single(filters['ackOverdue']) === 'yes'

  return (
    <Space wrap size="small">
      <Tooltip title={t('dataTable.closedHiddenHint')}>
        <Select
          style={{ minWidth: 175 }}
          aria-label={t('subscribers.columns.status')}
          value={status}
          onChange={(v: string) => set('status', v === STATE_OPEN_ONLY ? undefined : v)}
          options={closedAwareOptions(
            t('dataTable.stateOpenOnly'),
            t('dataTable.stateAll'),
            TICKET_STATUSES.map((s) => ({ value: s, label: t(`crm.ticketStatus.${s}`) })),
          )}
        />
      </Tooltip>
      <FilterDrawer<Filters>
        value={filters}
        resetValue={{ ...filters, priority: [], category: [], overdue: [], ackOverdue: [] }}
        activeCount={[category, priority, overdue ? 'yes' : undefined, ackOverdue ? 'yes' : undefined]
          .filter(Boolean).length}
        onApply={commit}
      >
        {(draft, patchDraft) => {
          const setDraftFilter = (key: string, value: string | undefined) =>
            patchDraft({ [key]: value ? [value] : [] })
          return (
            <>
              <Form.Item label={t('crm.ticket.priority')}>
                <Select
                  allowClear
                  style={{ width: '100%' }}
                  placeholder={t('crm.ticket.priority')}
                  value={single(draft['priority'])}
                  onChange={(v?: string) => setDraftFilter('priority', v)}
                  options={TICKET_PRIORITIES.map((p) => ({
                    value: p,
                    label: t(`crm.ticketPriority.${p}`),
                  }))}
                />
              </Form.Item>
              <Form.Item label={t('crm.ticket.category')}>
                <Select
                  allowClear
                  style={{ width: '100%' }}
                  placeholder={t('crm.ticket.category')}
                  value={single(draft['category'])}
                  onChange={(v?: string) => setDraftFilter('category', v)}
                  options={TICKET_CATEGORIES.map((c) => ({
                    value: c,
                    label: t(`crm.ticketCategory.${c}`),
                  }))}
                />
              </Form.Item>
              {/*
                ⚠️ «Faqat kechikkanlar» — SERVER bayrog'i (`onlyOverdue`), panelning
                o'z hisobi emas. Mijozda hisoblash faqat joriy sahifani filtrlardi,
                ya'ni "kechikkanlar yo'q" degan xulosa 2-sahifada yolg'on bo'lardi.
              */}
              <Form.Item>
                <Checkbox
                  checked={single(draft['overdue']) === 'yes'}
                  onChange={(e) => setDraftFilter('overdue', e.target.checked ? 'yes' : undefined)}
                >
                  {t('crm.ticket.onlyOverdue')}
                </Checkbox>
              </Form.Item>
              <Form.Item>
                <Checkbox
                  checked={single(draft['ackOverdue']) === 'yes'}
                  onChange={(e) => setDraftFilter('ackOverdue', e.target.checked ? 'yes' : undefined)}
                >
                  {t('crm.ticket.onlyAckOverdue')}
                </Checkbox>
              </Form.Item>
            </>
          )
        }}
      </FilterDrawer>
    </Space>
  )
}

/**
 * Muloqotlar filtri.
 *
 * ⚠️ QIDIRUV MAYDONI YO'Q: {@code InteractionQuery} da matn qidiruvi
 * umuman yo'q. Ishlamaydigan maydonni chizish "qidiruv buzuq" degan
 * xulosaga olib borardi ({@code fetchInteractionPage} izohi).
 *
 * ⚠️ Sana oynasi TENANT zonasida hisoblanadi ({@code rangeToIso}) —
 * brauzer zonasi tenantnikidan yarim soat farq qilganda oxirgi kun
 * jimgina tushib qolardi.
 */
export function InteractionFilterBar() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const { filters, set, clear } = useFilterPatch()
  const { patch, state } = useTableUrlState()

  const from = single(filters['from'])
  const to = single(filters['to'])
  const venue = single(filters['venue'])

  /*
    ⚠️ Ob'ektlar ro'yxati FAQAT `venue.read` bilan so'raladi. Huquqsiz
    rolda so'rov har sahifa ochilishida 403 berardi, tanlov esa baribir
    bo'sh bo'lardi — ya'ni foydasi yo'q, zarari bor.
  */
  const canReadVenues = can('venue.read')
  const venues = useQuery({
    queryKey: queryKeys.venues.list(null),
    queryFn: () => fetchVenues(null),
    enabled: canReadVenues,
  })

  const range: [Dayjs, Dayjs] | null = from && to ? [dayjs(from), dayjs(to)] : null

  const setRange = (value: [Dayjs, Dayjs] | null) => {
    const next = value ? rangeToIso(value) : null
    patch({
      page: 1,
      filters: {
        ...state.filters,
        from: next ? [next.from] : [],
        to: next ? [next.to] : [],
      },
    })
  }

  return (
    <Space wrap size="small">
      <DatePicker.RangePicker
        value={range}
        onChange={(value) => setRange(value && value[0] && value[1] ? [value[0], value[1]] : null)}
        allowClear
      />
      {canReadVenues ? (
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ minWidth: 220 }}
          placeholder={t('crm.interaction.linkVenue')}
          value={venue}
          loading={venues.isPending}
          onChange={(v?: string) => set('venue', v)}
          options={(venues.data ?? []).map((v) => ({ value: v.id, label: v.name }))}
        />
      ) : null}
      <ClearFilters count={[from, venue].filter(Boolean).length} onClear={clear} />
    </Space>
  )
}
